import React from "react";
import { Spin, Button, message, Row, Col } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import BaseForm from "../components/Form/BaseForm";

import { FormFieldConfig, Provider } from "../helpers/shared-interfaces";
import {
  request,
  responseHandler,
  generateDisplayErrorMessage,
  cleanStringArray,
} from "../helpers/utilities";
import { CREATE_PROVIDER } from "../gql/loans";

interface NewProviderProps {
  onCancel?: () => void;
  onSuccess?: (provider: Provider) => void;
}

interface NewProviderState {
  isSubmitting: boolean;
}

export class NewProvider extends React.Component<
  NewProviderProps,
  NewProviderState
> {
  formRef = React.createRef<FormInstance>();

  state: NewProviderState = {
    isSubmitting: false,
  };

  redirectToViewProviderPage(id: string): void {
    window.location.href = `/providers/${id}`;
  }

  createProvider = async (): Promise<void> => {
    interface Response {
      provider: Provider;
    }

    if (!this.formRef.current) return;

    this.setState({ isSubmitting: true });

    try {
      const data = await this.formRef.current.validateFields([
        "name",
        "contactEmail",
        "ccEmails",
      ]);

      const variables = {
        name: data.name,
        contactEmail: data.contactEmail || null,
        ccEmails: cleanStringArray(data.ccEmails),
      };

      const response: AxiosResponse = await request({
        query: CREATE_PROVIDER,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createProvider",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        this.formRef.current.resetFields();
        message.success("Provider created successfully!");

        if (this.props.onSuccess) {
          this.props.onSuccess(mutation.provider);
        }

        this.redirectToViewProviderPage(mutation.provider.id);
      }
    } catch (error) {
      message.error(`Failed to create provider - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { onCancel } = this.props;
    const { isSubmitting } = this.state;

    const fields: FormFieldConfig[] = [
      {
        id: "name",
        name: "name",
        inputType: "string",
        required: true,
        placeholder: "Enter provider name",
        rule: "string",
        label: "Provider Name",
        colConfig: { span: 24 },
      },
      {
        id: "contactEmail",
        name: "contactEmail",
        inputType: "string",
        required: false,
        placeholder: "Enter contact email",
        rule: "string",
        label: "Contact Email",
        colConfig: { span: 24 },
      },
      {
        id: "ccEmails",
        name: "ccEmails",
        inputType: "emailArray",
        required: false,
        placeholder: "Enter CC email",
        rule: "array",
        label: "CC Emails",
        initialValue: [""],
        colConfig: { span: 24 },
        elementStyle: { marginBottom: "8px", width: "100%" },
      },
    ];

    return (
      <Spin spinning={isSubmitting}>
        <div>
          <BaseForm
            layout="vertical"
            reference={this.formRef}
            fields={fields}
            fieldsToHide={[]}
          />

          <Row gutter={16} style={{ marginTop: 24 }}>
            {onCancel && (
              <Col span={12}>
                <Button
                  onClick={onCancel}
                  style={{ width: "100%" }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Col>
            )}
            <Col span={onCancel ? 12 : 24}>
              <Button
                type="primary"
                onClick={this.createProvider}
                loading={isSubmitting}
                style={{ width: "100%" }}
              >
                Create Provider
              </Button>
            </Col>
          </Row>
        </div>
      </Spin>
    );
  }
}
